/* TYT cookie consent — thin UI over Shopify's Customer Privacy API.

   ⚠ DO NOT rename this file back to anything containing "cookie-consent".
   It was tyt-cookie-consent.js until 2026-09-29, when Malwarebytes Browser
   Guard was found blocking it outright (ERR_BLOCKED_BY_CLIENT) — content
   blockers match consent-banner scripts on the URL, and the old name hit a
   standard filter rule. A blocked customer gets no banner at all, so can
   never opt IN: tracking stays off for them permanently. The file name is
   load-bearing; the mismatch with the section name is deliberate.

   The banner auto-shows only where Shopify says consent is required
   (Settings → Customer Privacy regions) AND no choice is recorded yet.
   The footer "Cookies" link ([data-tyt-cookie-prefs]) opens the
   preferences panel in every region. Consent is recorded via
   setTrackingConsent(), which gates Shopify analytics, checkout data
   and app web pixels — we never set tracking cookies ourselves.
   Cookie Policy promises kept here: X on the banner = reject
   non-essential (remembered, so we don't re-prompt); reject is as easy
   as accept; choices changeable any time. */
(() => {
  'use strict';

  const root = document.getElementById('tyt-cookie-consent');
  if (!root) return;

  const banner = root.querySelector('.tyt-cookie-banner');
  const prefs = root.querySelector('.tyt-cookie-prefs');
  const saleRow = root.querySelector('[data-tyt-consent-row="sale_of_data"]');
  const toggles = {};
  ['preferences', 'analytics', 'marketing', 'sale_of_data'].forEach((key) => {
    toggles[key] = root.querySelector(`input[data-tyt-consent="${key}"]`);
  });

  let cp = null; // window.Shopify.customerPrivacy once loaded
  let consentRequired = false;
  let lastFocus = null;

  // 'pending' until we know whether Shopify's Customer Privacy API is usable,
  // then latched to 'ready' or 'unavailable' — the first answer wins, so a late
  // timeout can never demote a load that actually succeeded.
  //
  // 'unavailable' is almost always a content blocker: Malwarebytes Browser
  // Guard blocks Shopify's own consent-tracking-api.js, which lives at a fixed
  // path on Shopify's CDN and cannot be renamed the way ours was. Without this
  // state the panel rendered its toggles and buttons as normal and every one of
  // them silently did nothing (`if (!cp) return`) — indistinguishable from a
  // broken shop. See DECISIONS.md 2026-09-29.
  let cpState = 'pending';
  const blockedNotice = root.querySelector('[data-tyt-consent-blocked]');
  const controlGroups = root.querySelectorAll('[data-tyt-consent-controls]');

  function renderAvailability() {
    const blocked = cpState === 'unavailable';
    if (blockedNotice) blockedNotice.hidden = !blocked;
    controlGroups.forEach((el) => { el.hidden = blocked; });
  }

  function setCpState(state) {
    if (cpState !== 'pending') return; // latch
    cpState = state;
    renderAvailability();
  }

  // currentVisitorConsent() values are 'yes' | 'no' | '' (undecided).
  const undecided = (c) => !c || (!c.marketing && !c.analytics && !c.preferences && !c.sale_of_data);

  const showBanner = () => { banner.hidden = false; };
  const hideBanner = () => { banner.hidden = true; };

  function openPrefs() {
    syncToggles();
    renderAvailability();
    lastFocus = document.activeElement;
    prefs.hidden = false;
    document.documentElement.classList.add('tyt-cookie-lock');
    const first = prefs.querySelector('.tyt-cookie-prefs__close');
    if (first) first.focus();
  }

  function closePrefs() {
    prefs.hidden = true;
    document.documentElement.classList.remove('tyt-cookie-lock');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
  }

  function syncToggles() {
    if (!cp) return;
    const current = cp.currentVisitorConsent() || {};
    // Undecided ('') shows as ON where tracking runs by default and OFF
    // where the region requires consent first — mirrors what Shopify does.
    const fallback = !consentRequired;
    Object.keys(toggles).forEach((key) => {
      const input = toggles[key];
      if (!input) return;
      const value = current[key] || '';
      input.checked = value === 'yes' || (value === '' && fallback);
    });
    if (saleRow) {
      const saleApplies = !!(cp.saleOfDataRegion && cp.saleOfDataRegion());
      saleRow.hidden = !saleApplies;
      // Reflect a Global Privacy Control signal (Shopify honours it server-side).
      if (saleApplies && navigator.globalPrivacyControl && (current.sale_of_data || '') === '' && toggles.sale_of_data) {
        toggles.sale_of_data.checked = false;
      }
    }
  }

  function applyConsent(consent) {
    if (!cp) return;
    cp.setTrackingConsent(consent, (result) => {
      // On failure consent stays undecided, so consent-required regions keep
      // blocking tracking and the banner re-asks next page load — safe both
      // ways. (Known to fail on `shopify theme dev` localhost; fine on the
      // real domain.)
      if (result && result.error) console.warn('TYT cookie consent: save failed —', result.error);
      hideBanner();
      closePrefs();
    });
  }

  const acceptAll = () => applyConsent({ marketing: true, analytics: true, preferences: true, sale_of_data: true });
  const rejectAll = () => applyConsent({ marketing: false, analytics: false, preferences: false, sale_of_data: false });

  function saveChoices() {
    const marketing = !!(toggles.marketing && toggles.marketing.checked);
    // When the US row is hidden the visitor was never asked — mirror marketing.
    const saleVisible = saleRow && !saleRow.hidden && toggles.sale_of_data;
    applyConsent({
      preferences: !!(toggles.preferences && toggles.preferences.checked),
      analytics: !!(toggles.analytics && toggles.analytics.checked),
      marketing: marketing,
      sale_of_data: saleVisible ? toggles.sale_of_data.checked : marketing,
    });
  }

  // ── wiring ──
  root.querySelectorAll('[data-tyt-cookie-accept]').forEach((el) => el.addEventListener('click', acceptAll));
  root.querySelectorAll('[data-tyt-cookie-reject]').forEach((el) => el.addEventListener('click', rejectAll));
  root.querySelectorAll('[data-tyt-cookie-close]').forEach((el) => el.addEventListener('click', rejectAll));
  root.querySelectorAll('[data-tyt-cookie-manage]').forEach((el) => el.addEventListener('click', openPrefs));
  root.querySelectorAll('[data-tyt-cookie-save]').forEach((el) => el.addEventListener('click', saveChoices));
  root.querySelectorAll('[data-tyt-cookie-prefs-close]').forEach((el) => el.addEventListener('click', closePrefs));

  // Footer "Cookies" link (and any other element carrying the attribute).
  document.querySelectorAll('[data-tyt-cookie-prefs]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      openPrefs();
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !prefs.hidden) closePrefs();
  });

  // Keep the panel truthful if consent changes elsewhere (e.g. another tab).
  document.addEventListener('visitorConsentCollected', () => {
    if (!prefs.hidden) syncToggles();
  });

  // Theme editor: never auto-show; preview the banner by selecting the section.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(root)) showBanner();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(root)) hideBanner();
    });
  }

  // ── boot ──
  // ⚠ The timeout is not belt-and-braces. When the API script is blocked,
  // loadFeatures may never invoke its callback AT ALL rather than calling it
  // with an error — so the error branch below cannot be relied on to notice.
  // Without the timeout the panel would sit in 'pending' forever, showing live
  // controls that do nothing, which is the exact bug this is here to fix.
  const API_TIMEOUT_MS = 4000;

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    setCpState('unavailable');
    return;
  }
  setTimeout(() => setCpState('unavailable'), API_TIMEOUT_MS);
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error || !window.Shopify.customerPrivacy) {
      setCpState('unavailable');
      return;
    }
    cp = window.Shopify.customerPrivacy;
    setCpState('ready');
    consentRequired = !!cp.shouldShowBanner();
    const saleOfData = !!(cp.saleOfDataRegion && cp.saleOfDataRegion());
    // Opt-in banner only for consent-first regimes (GDPR-style). US state
    // opt-out regimes get the footer link + sale-of-data row + GPC instead.
    // (getRegulation() would say this directly but is deprecated.)
    const optIn = consentRequired && !saleOfData;
    if (!window.Shopify.designMode && optIn && undecided(cp.currentVisitorConsent())) {
      showBanner();
    }
  });
})();
